
// 🌐 SEO 工具函數 - ONGU Studio

/**
 * 生成規範 URL
 */
export const getCanonicalUrl = (path: string = ''): string => {
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `https://www.ongucoffee.com${cleanPath}`;
};

/**
 * 檢查並重定向到規範域名
 */
export const checkDomainRedirect = (): void => {
  if (typeof window === 'undefined') return;
  
  const currentHost = window.location.host;
  const currentPath = window.location.pathname + window.location.search + window.location.hash;
  
  // 如果不是 www.ongucoffee.com，重定向
  if (currentHost === 'ongucoffee.com') {
    window.location.replace(`https://www.ongucoffee.com${currentPath}`);
  }
  // 確保 HTTPS
  else if (window.location.protocol === 'http:' && currentHost === 'www.ongucoffee.com') {
    window.location.replace(`https://www.ongucoffee.com${currentPath}`);
  }
};

/**
 * 設置頁面 meta 標籤
 */
export const setPageMeta = (options: {
  title: string;
  description: string;
  path?: string;
  image?: string;
  type?: string;
}) => {
  const { title, description, path = '', image, type = 'website' } = options;
  const canonicalUrl = getCanonicalUrl(path);
  
  // 設置 title
  document.title = `${title} | ONGU Studio 醞玖製研`;
  
  // 設置或更新 meta 標籤
  const setMeta = (name: string, content: string, property = false) => {
    const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
    let meta = document.querySelector(selector) as HTMLMetaElement;
    
    if (!meta) {
      meta = document.createElement('meta');
      if (property) {
        meta.setAttribute('property', name);
      } else {
        meta.setAttribute('name', name);
      }
      document.head.appendChild(meta);
    }
    
    meta.setAttribute('content', content);
  };
  
  // 設置 canonical link
  let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }
  canonical.setAttribute('href', canonicalUrl);
  
  // 基本 SEO 標籤
  setMeta('description', description);
  setMeta('robots', 'index, follow');
  
  // Open Graph 標籤
  setMeta('og:title', title, true);
  setMeta('og:description', description, true);
  setMeta('og:url', canonicalUrl, true);
  setMeta('og:type', type, true);
  setMeta('og:site_name', 'ONGU Studio 醞玖製研', true);
  
  if (image) {
    setMeta('og:image', image, true);
  }
  
  // Twitter Card 標籤
  setMeta('twitter:card', 'summary_large_image');
  setMeta('twitter:title', title);
  setMeta('twitter:description', description);
  
  if (image) {
    setMeta('twitter:image', image);
  }

  // 設置多語言 Alternate 標籤 (hreflang)
  const setAlternateLink = (hreflang: string, href: string) => {
    let link = document.querySelector(`link[rel="alternate"][hreflang="${hreflang}"]`) as HTMLLinkElement;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', hreflang);
      document.head.appendChild(link);
    }
    link.setAttribute('href', href);
  };

  // 解析基礎路徑（移除語言前綴）
  const currentPath = path || window.location.pathname;
  // 移除 /zh 或 /en 前綴，保留後續路徑
  // 例如: /zh/shop -> /shop, /en/about -> /about, /zh -> /, / -> /
  const baseRoute = currentPath.replace(/^\/(zh|en)(\/|$)/, '/').replace(/\/$/, '') || '/';
  
  const domain = 'https://www.ongucoffee.com';
  
  // 生成各語言連結
  // 注意：如果是根路徑 '/'，則 url 結尾不加 '/'，例如 '.../zh'
  const zhUrl = `${domain}/zh${baseRoute === '/' ? '' : baseRoute}`;
  const enUrl = `${domain}/en${baseRoute === '/' ? '' : baseRoute}`;

  setAlternateLink('zh-TW', zhUrl);
  setAlternateLink('en-US', enUrl);
  setAlternateLink('x-default', zhUrl); // 預設語言為中文
};

/**
 * 生成結構化資料
 */
export const generateStructuredData = (data: any) => {
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(data);
  
  // 移除舊的結構化資料
  const existing = document.querySelector('script[type="application/ld+json"]');
  if (existing) {
    existing.remove();
  }
  
  document.head.appendChild(script);
};
